import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Button, IconButton } from "@mui/material";
import TagTreeSelect from "./TagTreeSelect";
import { updateRetrotagTagPath } from "store/retrotagSlice";
import { useDispatch } from "react-redux";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  FormPageStyledForm,
  SubmittedPageStyles,
} from "./RetrotagPopperStyles";
import { useIsDarkMode } from "store/atoms";
import { useAtom } from "jotai";
import { HubletTag } from "components/Dashboard/TrendChart/TrendChartContent/SupervisorContext/supervisorContextUtils";
import { hubletTagsAtom } from "./retrotagQueryHooks";
import Spinner from "components/SimpleComponents/Spinner/Spinner";

export function RetrotagPopperSubmittedPage({
  errorMsg,
}: {
  errorMsg: string | null;
}) {
  const feedbackText = errorMsg
    ? "Reallocation Failed"
    : "Reallocation Complete";
  return (
    <SubmittedPageStyles className="submittedPage">
      {errorMsg ? (
        <ErrorIcon fontSize="large" color="secondary" />
      ) : (
        <CheckCircleIcon fontSize="large" color="primary" />
      )}
      <h2 className="feedbackText">{feedbackText}</h2>
      {errorMsg ? <p className="errorMsg">{errorMsg}</p> : null}
    </SubmittedPageStyles>
  );
}

export function RetrotagPopperFormPage({
  handleClose,
  handleFormSubmit,
  processId,
  loadingResponse,
}: {
  handleClose: () => void;
  handleFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  processId: string;
  loadingResponse: boolean;
}) {
  const [replacementTag, setReplacementTag] = React.useState(
    null as string | null
  );
  const dispatch = useDispatch();
  const { translate } = useFlightTranslation();
  const [isDarkMode] = useIsDarkMode();

  const handleTagSelect = (tag: HubletTag) => {
    dispatch(updateRetrotagTagPath(tag.tagPath));
    setReplacementTag(tag.tagName);
  };

  return (
    <FormPageStyledForm onSubmit={handleFormSubmit} $isDarkMode={isDarkMode}>
      <ClosePopperButton handleClose={handleClose} />
      <h2 className="formTitle" id="form-title">
        {translate("Reallocate_Downtime")}
      </h2>
      <div className="space space-15" style={{ height: 15 }} />
      <div className="formDescription">
        <p id="form-description">
          {translate(
            `Select_a_segment_you'd_like_to_re-tag._Drag_the_edges_to_be_more_precise.`
          )}
        </p>
      </div>
      <div className="space space-25" style={{ height: 25 }} />
      <TagTree {...{ handleTagSelect, processId, replacementTag }} />
      <div className="space space-10" style={{ height: 10 }} />

      <div className="bottomButtons">
        <Button className="cancelButton" type="button" onClick={handleClose}>
          {translate("Cancel")}
        </Button>
        <Button
          className="confirmButton"
          type="submit"
          disabled={!replacementTag || loadingResponse}
          variant={!replacementTag ? "text" : "contained"}
        >
          {loadingResponse ? (
            <div
              style={{
                height: "24px",
                width: "52px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spinner scale={0.2} />
            </div>
          ) : (
            translate("Confirm")
          )}
        </Button>
      </div>
    </FormPageStyledForm>
  );
}

function ClosePopperButton({ handleClose }: { handleClose: () => void }) {
  return (
    <div className="btnClose">
      <IconButton type="button" onClick={handleClose} size="large">
        <CloseIcon style={{ color: "#000" }} />
      </IconButton>
    </div>
  );
}

function TagTree({
  handleTagSelect,
  processId,
  replacementTag,
}: {
  handleTagSelect: (tag: HubletTag) => void;
  processId: string;
  replacementTag: string | null;
}) {
  // Use the hublet tags from the global state instead of querying again
  const [hubletTagsMap] = useAtom(hubletTagsAtom);

  // Get the tags for this process
  const hubletTags = hubletTagsMap[processId];

  return (
    <TagTreeSelect
      tagValue={replacementTag}
      hubletTags={hubletTags}
      onTagSelect={handleTagSelect}
    />
  );
}
